import {
  addDays,
  format,
  parseISO,
  isValid,
  subDays,
} from "date-fns";

export const TOURNAMENT_NAME_REGEX = /^[a-zA-Z0-9]+(?: [a-zA-Z0-9]+)*$/;

/** Match `tbl_tournaments` VARCHAR limits (see `backend/utils/tournamentValidation.js`). */
export const TOURNAMENT_NAME_MAX_LEN = 150;
export const TOURNAMENT_LOCATION_MAX_LEN = 200;
export const ORGANIZER_NAME_MAX_LEN = 150;
export const TOURNAMENT_TIME_MAX_LEN = 8;
export const TOURNAMENT_DESCRIPTION_MAX_LEN = 60000;

export function sanitizeTournamentNameInput(raw) {
  if (raw == null) return "";
  return String(raw).replace(/\s+/g, " ");
}

export function todayYmd() {
  return format(new Date(), "yyyy-MM-dd");
}

export function tomorrowYmd() {
  return format(addDays(new Date(), 1), "yyyy-MM-dd");
}

/** YYYY-MM-DD for date inputs — uses local calendar day (not UTC ISO slice). */
export function dateInputYmdFromValue(value) {
  if (value == null || value === "") return "";
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const dmy = raw.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
  if (dmy) {
    return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
  }
  const d =
    value instanceof Date
      ? value
      : new Date(/^\d{4}-\d{2}-\d{2}/.test(raw) ? raw.replace(" ", "T") : raw);
  if (Number.isNaN(d.getTime())) {
    const pre = raw.match(/^(\d{4}-\d{2}-\d{2})/);
    return pre ? pre[1] : "";
  }
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Day before tournament (YYYY-MM-DD), or null if invalid */
export function registrationLastDateMaxYmd(tournamentYmd) {
  if (!tournamentYmd || !/^\d{4}-\d{2}-\d{2}$/.test(tournamentYmd)) return null;
  try {
    const d = parseISO(`${tournamentYmd}T12:00:00`);
    if (!isValid(d)) return null;
    return format(subDays(d, 1), "yyyy-MM-dd");
  } catch {
    return null;
  }
}

export function normalizeOrganizerNameInput(raw) {
  if (raw == null) return "";
  return String(raw).trim().replace(/\s+/g, " ").replace(/\.+/g, ".");
}

/** HH:MM for duplicate checks (aligned with backend TIME_FORMAT '%H:%i'). */
export function normalizeTournamentTimeForCompare(raw) {
  if (raw == null || raw === "") return "";
  const s = String(raw).trim();
  const m = s.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return "";
  const h = String(parseInt(m[1], 10)).padStart(2, "0");
  const min = m[2].padStart(2, "0");
  return `${h}:${min}`;
}

/**
 * Active tournament with same normalized name, location, and time (excludes excludeId when editing).
 */
export function findDuplicateTournamentInList(payload, rows, { excludeId } = {}) {
  const name = sanitizeTournamentNameInput(payload.tournament_name || "").trim().toLowerCase();
  const loc = String(payload.tournament_location || "").trim().toLowerCase();
  const time = normalizeTournamentTimeForCompare(payload.tournament_time);
  if (!name || !loc || !time) return null;
  for (const t of rows || []) {
    if (excludeId != null && Number(t.id) === Number(excludeId)) continue;
    const tn = sanitizeTournamentNameInput(t.tournament_name || "").trim().toLowerCase();
    const tl = String(t.tournament_location || "").trim().toLowerCase();
    const tt = normalizeTournamentTimeForCompare(t.tournament_time);
    if (tn === name && tl === loc && tt === time) return t;
  }
  return null;
}

function validateOrganizerNameClient(raw) {
  const o = normalizeOrganizerNameInput(raw);
  if (!o) return { ok: true, value: "" };
  if (o.length > ORGANIZER_NAME_MAX_LEN) {
    return {
      ok: false,
      error: `Organizer name must be at most ${ORGANIZER_NAME_MAX_LEN} characters`,
    };
  }
  if (/[0-9]/.test(o)) return { ok: false, error: "Organizer name cannot include numbers" };
  if (/\.\./.test(o)) return { ok: false, error: "Use only single dots in organizer name" };
  if (!/^[\p{L}\s.]+$/u.test(o)) {
    return { ok: false, error: "Organizer name may only include letters, spaces, and dots" };
  }
  return { ok: true, value: o };
}

function validateContactClient(value, organizerRequired) {
  const v = value != null ? String(value).replace(/\D/g, "") : "";
  if (!organizerRequired) {
    if (!v) return { ok: true, value: "" };
    if (v.length !== 10) return { ok: false, error: "Organizer contact must be 10 digits" };
    return { ok: true, value: v };
  }
  if (v.length !== 10) {
    return { ok: false, error: "Organizer contact number is required (10 digits)" };
  }
  return { ok: true, value: v };
}

function compareYmd(a, b) {
  if (!a || !b) return null;
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

/**
 * Client-side validation (mirrors backend rules).
 * @param {object} formData
 * @param {{ isEdit?: boolean }} options — when false/new, tournament date must be after today
 */
export function validateTournamentForm(formData, { isEdit = false } = {}) {
  const err = {};
  const name = sanitizeTournamentNameInput(formData.tournament_name).trim();
  if (!name) err.tournament_name = "Tournament name is required";
  else if (name.length < 2) err.tournament_name = "Tournament name is too short";
  else if (name.length > TOURNAMENT_NAME_MAX_LEN) {
    err.tournament_name = `Tournament name must be at most ${TOURNAMENT_NAME_MAX_LEN} characters`;
  } else if (!TOURNAMENT_NAME_REGEX.test(name)) {
    err.tournament_name =
      "Use only letters and numbers; single spaces between words (no special characters)";
  }

  if (!formData.sport_id) err.sport_id = "Sport is required";
  if (!formData.tournament_level_id) err.tournament_level_id = "Tournament level is required";

  const td = formData.tournament_date ? String(formData.tournament_date).slice(0, 10) : "";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(td)) err.tournament_date = "Tournament date is required";

  const tt = formData.tournament_time != null ? String(formData.tournament_time).trim() : "";
  if (!tt) err.tournament_time = "Tournament time is required";
  else if (tt.length > TOURNAMENT_TIME_MAX_LEN) {
    err.tournament_time = `Tournament time must be at most ${TOURNAMENT_TIME_MAX_LEN} characters`;
  }

  const loc = formData.tournament_location != null ? String(formData.tournament_location).trim() : "";
  if (!loc) err.tournament_location = "Location is required";
  else if (loc.length > TOURNAMENT_LOCATION_MAX_LEN) {
    err.tournament_location = `Location must be at most ${TOURNAMENT_LOCATION_MAX_LEN} characters`;
  }

  const rl = formData.registration_last_date ? String(formData.registration_last_date).slice(0, 10) : "";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(rl)) {
    err.registration_last_date = "Registration last date is required";
  }

  if (
    formData.participation_fee === "" ||
    formData.participation_fee === null ||
    formData.participation_fee === undefined
  ) {
    err.participation_fee = "Participation fee is required";
  } else {
    const fee = Number(formData.participation_fee);
    if (Number.isNaN(fee) || fee < 0) err.participation_fee = "Participation fee cannot be negative";
  }

  if (
    formData.max_students_allowed === "" ||
    formData.max_students_allowed === null ||
    formData.max_students_allowed === undefined
  ) {
    err.max_students_allowed = "Maximum students is required";
  } else {
    const m = parseInt(String(formData.max_students_allowed), 10);
    if (Number.isNaN(m) || m < 2) err.max_students_allowed = "Maximum students must be at least 2";
  }

  const org = validateOrganizerNameClient(formData.organizer_name);
  if (!org.ok) err.organizer_name = org.error;
  const hasOrganizer = org.ok && Boolean(org.value);
  const contact = validateContactClient(formData.contact_number, hasOrganizer);
  if (!contact.ok) err.contact_number = contact.error;

  const today = todayYmd();
  if (!err.tournament_date && td) {
    if (!isEdit && compareYmd(td, today) <= 0) {
      err.tournament_date = "Tournament date must be after today";
    }
  }

  if (!err.registration_last_date && rl && !err.tournament_date && td) {
    const tourAhead = compareYmd(td, today) > 0;
    if (tourAhead) {
      if (compareYmd(rl, today) <= 0) {
        err.registration_last_date = "Registration last date must be after today";
      } else if (compareYmd(rl, td) >= 0) {
        err.registration_last_date = "Registration last date must be before tournament date";
      }
    } else if (compareYmd(rl, td) >= 0) {
      err.registration_last_date = "Registration last date must be before tournament date";
    }
  }

  if (
    formData.tournament_update_id === "" ||
    formData.tournament_update_id === null ||
    formData.tournament_update_id === undefined
  ) {
    err.tournament_update_id = "Tournament update is required";
  } else {
    const uid = parseInt(String(formData.tournament_update_id), 10);
    if (Number.isNaN(uid) || uid < 1) err.tournament_update_id = "Select a valid tournament update";
  }

  const desc =
    formData.description != null && String(formData.description).trim() !== ""
      ? String(formData.description)
      : "";
  if (desc.length > TOURNAMENT_DESCRIPTION_MAX_LEN) {
    err.description = `Description must be at most ${TOURNAMENT_DESCRIPTION_MAX_LEN} characters`;
  }

  return err;
}
